import {
  DealListItemResponse,
  DealRequestType,
  updateDeal,
  UpdateDealRequest
} from 'app/console-api';
import {
  BodyText,
  Button,
  Dropdown,
  FlexColumn,
  FlexRow,
  SvgIcon,
  Textarea
} from '@make-software/cspr-design';
import { DropdownValue } from '@make-software/cspr-design/dist/lib/components/dropdown/dropdown';
import React, { useEffect, useState } from 'react';
import { RewardsType } from 'app/console-api/apps/types';
import styled from 'styled-components';
import { HttpError } from 'app/components/utils/http-error';
import { AUTH_TOKEN, customErrorMessage, statusCode } from 'app/constants';
import { APPS_DEALS_VIEW_PATH, SIGN_IN_PATH } from 'app/route/paths';
import SuccessEntityWindow from 'app/components/common/success-entity-modal/success-entity-modal';
import RejectedSuccess from 'assets/logos/rejected-request-success.svg';
import SuccessUpdatedIcon from 'assets/logos/success-entity.svg';
import { ErrorResult } from 'app/organizations-api/types';
import { ErrorWindow } from 'app/components/common/error-window/error-window';
import NotFoundIcon from 'assets/logos/no-found.svg';
import ErrorEntityWindow from 'app/components/common/error-entity-modal/error-entity-modal';
import { AppCategory } from 'app/console-api/apps/categories';
import { AppCategoriesSelect } from 'app/components/management/common/app-categories-select/app-categories-select';
import {
  ButtonsContainer,
  ErrorText,
  ErrorTextContainer,
  StyledCaption,
  StyledDismissButton,
  StyledInput
} from 'app/components/common/common-styles';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { createUpdateDealValidationSchema } from 'app/components/management/apps/deals/common/deals-validation-schemas';
import { DealStatus } from 'app/components/common/deals-statuses/deals-statuses';
import { generatePath, useNavigate } from 'react-router-dom';
import {
  CustomMetadataField,
  CategoriesField,
  RewardsField
} from '../common/deal-form-fields';

interface DealViewSettingsPendingFormProps {
  data: DealListItemResponse;
  categoryIds: number[];
  appCategories: AppCategory[];
  rewardsTypes: RewardsType[];
}

export const StyledConfirmButton = styled(Button)(({ theme }) =>
  theme.withMedia({
    width: ['100%', '200px', '200px'],
    backgroundColor: theme.styleguideColors.fillPrimaryBlue,
    ':hover': {
      background: theme.styleguideColors.fillPrimaryBlueHover
    },
    ':active': {
      background: theme.styleguideColors.fillPrimaryBlueClick
    }
  })
);

const ButtonRow = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    marginTop: '40px'
  })
);

const StyledTextArea = styled(Textarea)(({ theme }) =>
  theme.withMedia({
    width: ['300px', '400px', '500px']
  })
);

export const DealEditFormAwaiting = ({
  data,
  categoryIds,
  appCategories,
  rewardsTypes
}: DealViewSettingsPendingFormProps) => {
  const statusOptions: DropdownValue[] = [
    { label: 'Waiting for Approval', value: DealStatus.Pending },
    { label: 'Approved', value: DealStatus.Active },
    { label: 'Rejected', value: DealStatus.Rejected }
  ];
  const [statusControl, setStatusControl] = useState<DropdownValue>(
    statusOptions.find((option) => option.value === data.dealStatus) as DropdownValue
  );
  const [openSuccessUpdateModal, setOpenSuccessUpdateModal] = useState<boolean>(false);
  const [rejectionReason, setRejectionReason] = useState<string>();
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);
  const [openConflictErrorModal, setOpenConflictErrorModal] = useState<boolean>(false);
  const [conflictError, setConflictError] = useState<string>('');
  const navigate = useNavigate();

  const onDismiss = () => {
    navigate(generatePath(APPS_DEALS_VIEW_PATH, { appID: data.myApp.id, id: data.dealDetails.id }));
  };

  const [approvedFormData, setApprovedFormData] = useState({
    title: data.dealDetails.title,
    customMetadata: data.dealDetails.customMetadata
      ? JSON.stringify(data.dealDetails.customMetadata, null, 2)
      : '',
    rewards: data.rewards || [],
    categories: categoryIds
  });

  useEffect(() => {
    if (rewardsTypes.length === 0) return;
    handleInputChange('rewards', data.rewards || [{ rewardID: rewardsTypes[0]?.id, value: 100 }]);
  }, [rewardsTypes]);

  useEffect(() => {
    handleInputChange('categories', categoryIds);
  }, [categoryIds]);

  const {
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(createUpdateDealValidationSchema(data.dealDetails.tradeType)),
    defaultValues: approvedFormData
  });

  const handleInputChange = <K extends keyof typeof approvedFormData>(
    key: K,
    value: (typeof approvedFormData)[K]
  ) => {
    setApprovedFormData((prev) => ({
      ...prev,
      [key]: value
    }));
    // @ts-ignore
    setValue(key, value);
    // @ts-ignore
    clearErrors(key);
  };

  const processUpdateDeal = (payload: UpdateDealRequest) => {
    updateDeal(data.dealDetails.appID, data.dealDetails.id, payload)
      .then(() => {
        setOpenSuccessUpdateModal(true);
      })
      .catch((error: any) => {
        if (error instanceof HttpError) {
          switch (error.statusCode) {
            case statusCode.validation_error:
              setUnexpectedError({
                code: error.statusCode.toString(),
                message: error.message
              });
              break;
            case statusCode.conflict_error:
              setConflictError(error.info?.error.description || error.message);
              setOpenConflictErrorModal(true);
              break;
            case statusCode.authentication_error:
              localStorage.removeItem(AUTH_TOKEN);
              window.location.href = SIGN_IN_PATH;
              return;
            case statusCode.unexpected_error:
              setUnexpectedError(customErrorMessage);
              break;
            default:
              setUnexpectedError(customErrorMessage);
              break;
          }
        } else {
          setUnexpectedError(customErrorMessage);
        }
        console.error('Failed to update deals:', error);
      });
  };

  const onSubmit = () => {
    if (statusControl.value === DealStatus.Rejected) {
      processUpdateDeal({
        dealStatus: statusControl.value as DealStatus,
        statusDescription: rejectionReason || 'No reason provided',
        title: data.dealDetails.title,
        customMetadata: data.dealDetails.customMetadata,
        rewards: data.rewards,
        categories: categoryIds
      });
      return;
    }
    handleSubmit(processSubmit)();
  };

  const processSubmit = () => {
    processUpdateDeal({
      dealStatus: statusControl.value as DealStatus,
      title: approvedFormData.title,
      customMetadata: approvedFormData.customMetadata
        ? JSON.parse(approvedFormData.customMetadata)
        : null,
      rewards: approvedFormData.rewards,
      categories: approvedFormData.categories
    });
  };

  const handleCloseUpdateDealModal = () => {
    setOpenSuccessUpdateModal(false);
    navigate(generatePath(APPS_DEALS_VIEW_PATH, { appID: data.myApp.id, id: data.dealDetails.id }));
  };

  const handleCloseConflictErrorModal = () => {
    setOpenConflictErrorModal(false);
  };

  if (unexpectedError) {
    return (
      <ErrorWindow
        withModal
        title={unexpectedError.code}
        information={unexpectedError.message}
        icon={<SvgIcon src={NotFoundIcon} width={219} height={125} />}
      />
    );
  }

  return (
    <FlexColumn gap={32}>
      <SuccessEntityWindow
        dismissLabel={'Close'}
        onDismiss={handleCloseUpdateDealModal}
        isOpen={openSuccessUpdateModal}
        icon={
          statusControl.value === DealStatus.Rejected ? (
            <SvgIcon src={RejectedSuccess} width={195} height={120} />
          ) : (
            <SvgIcon src={SuccessUpdatedIcon} width={195} height={120} />
          )
        }
        title={
          statusControl.value === DealStatus.Rejected
            ? 'Request has been rejected'
            : 'Deal accepted'
        }
        information={
          statusControl.value === DealStatus.Rejected
            ? 'Thank you for the review. We will notify a partner about your decision.'
            : 'Congratulations! The new partnership has been successfully established.'
        }
      />
      <ErrorEntityWindow
        dismissLabel={'Close'}
        onDismiss={handleCloseConflictErrorModal}
        isOpen={openConflictErrorModal}
        icon={<SvgIcon src={NotFoundIcon} width={195} height={120} />}
        title={conflictError}
        information={''}
      />
      <FlexColumn gap={8}>
        <BodyText size={1}>Request status:</BodyText>
        <Dropdown
          items={statusOptions}
          value={statusControl}
          onSelect={(value) => {
            setStatusControl(value as unknown as DropdownValue);
          }}
        />
      </FlexColumn>
      {statusControl.value === DealStatus.Rejected && (
        <FlexColumn>
          <BodyText size={1}>Reason</BodyText>
          <StyledTextArea placeholder={'Type rejection reason here'} value={rejectionReason} />
        </FlexColumn>
      )}
      {(statusControl.value === DealStatus.Active ||
        (statusControl.value === DealStatus.Pending &&
          data.dealDetails.requestType === DealRequestType.Outgoing)) && (
        <>
          <StyledInput
            required={true}
            label={<BodyText size={1}>Title</BodyText>}
            placeholder={'Title'}
            value={approvedFormData.title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleInputChange('title', e.target.value);
            }}
          />
          {errors?.title?.message && (
            <ErrorTextContainer justify={'flex-start'}>
              <ErrorText size={3}>{errors?.title?.message}</ErrorText>
            </ErrorTextContainer>
          )}

          <CustomMetadataField
            tradeType={data.dealDetails.tradeType}
            formData={approvedFormData}
            errors={errors}
            onFieldChange={(field: any, value: any) => handleInputChange(field as any, value)}
          />

          <CategoriesField
            tradeType={data.dealDetails.tradeType}
            formData={approvedFormData}
            errors={errors}
            appCategories={{
              data: appCategories || [],
              item_count: appCategories?.length || 0,
              page_count: 1
            }}
            onFieldChange={(field: any, value: any) => handleInputChange(field as any, value)}
          />

          <RewardsField
            tradeType={data.dealDetails.tradeType}
            formData={approvedFormData}
            errors={errors}
            rewardsTypes={rewardsTypes}
            onFieldChange={(field: any, value: any) => handleInputChange(field as any, value)}
          />
        </>
      )}
      <ButtonRow gap={40}>
        <ButtonsContainer justify={'space-between'} gap={16}>
          <StyledDismissButton color={'utility'} onClick={onDismiss}>
            Cancel
          </StyledDismissButton>
          <StyledConfirmButton onClick={onSubmit}>Save</StyledConfirmButton>
        </ButtonsContainer>
      </ButtonRow>
    </FlexColumn>
  );
};
